import type { ComponentConfig } from '@measured/puck';
import { Container } from '../layout/Container';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { FAQPageLd } from '../seo/jsonld/FAQPage';
import { Reveal } from '../motion/Reveal';

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqSplitProps {
  eyebrow?: string;
  title?: string;
  description?: string;
  contactLabel?: string;
  contactHref?: string;
  items: FaqItem[];
}

export function FaqSplit({
  eyebrow = 'Vos questions',
  title = 'On vous explique avant de vous facturer.',
  description = 'Si une réponse manque, écrivez-nous. On vous répond sous 24 h ouvrées avec sources documentées.',
  contactLabel = 'Poser une question →',
  contactHref = '/contact',
  items,
}: FaqSplitProps) {
  return (
    <section className="bg-surface py-20 md:py-28">
      <Container>
        <div className="grid gap-12 lg:grid-cols-[5fr,7fr] lg:gap-16">
          <Reveal>
            <div className="lg:sticky lg:top-28 lg:self-start">
              <p className="eyebrow">{eyebrow}</p>
              <h2 className="mt-4 text-display-3 font-display font-bold tracking-tight text-balance text-ink">
                {title}
              </h2>
              <p className="mt-5 max-w-md text-stone-600 leading-relaxed">{description}</p>
              <a
                href={contactHref}
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-700 transition-colors hover:text-brand-800"
              >
                {contactLabel}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-stone-200/70 bg-paper p-2">
              <Accordion type="single" collapsible className="divide-y divide-stone-200/70">
                {items.map((item, idx) => (
                  <AccordionItem
                    key={item.question}
                    value={`q-${idx}`}
                    className="border-0 px-5 last:border-0"
                  >
                    <AccordionTrigger className="py-5 text-left text-base font-semibold text-ink hover:no-underline md:text-lg">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 pr-6 text-base text-stone-600 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        </div>
        <FAQPageLd items={items} />
      </Container>
    </section>
  );
}

export const FaqSplitConfig: ComponentConfig<FaqSplitProps> = {
  fields: {
    eyebrow: { type: 'text' },
    title: { type: 'textarea' },
    description: { type: 'textarea' },
    contactLabel: { type: 'text' },
    contactHref: { type: 'text' },
    items: {
      type: 'array',
      arrayFields: { question: { type: 'text' }, answer: { type: 'textarea' } },
      defaultItemProps: { question: '', answer: '' },
    },
  },
  defaultProps: {
    eyebrow: 'Vos questions',
    title: 'On vous explique avant de vous facturer.',
    description:
      'Si une réponse manque, écrivez-nous. On vous répond sous 24 h ouvrées avec sources documentées.',
    contactLabel: 'Poser une question →',
    contactHref: '/contact',
    items: [
      {
        question: 'Sur le courtage énergie, êtes-vous indépendants des fournisseurs ?',
        answer:
          'Oui : sur le pôle courtage gaz/électricité B2B, nous sommes indépendants des fournisseurs (TotalEnergies, Engie, EDF Pro, Eni, Alpiq, Vattenfall…). Aucun partenariat exclusif, nous comparons toutes les offres accessibles à votre profil.',
      },
      {
        question: 'Sur les primes énergie, comment fonctionnent vos partenariats ?',
        answer:
          'En tant qu\'intermédiaire agréé, nous travaillons avec plusieurs fournisseurs d\'énergie (TotalEnergies, EDF, Engie et d\'autres). Ces partenariats sont transparents et détaillés dans nos conditions générales.',
      },
      {
        question: 'Combien coûtent vos services ?',
        answer:
          'Le diagnostic est gratuit et sans engagement. Pour les artisans, aucune avance. Tout est documenté dans nos conditions générales. Aucun frais caché.',
      },
      {
        question: 'Puis-je cumuler toutes les aides ?',
        answer:
          'Oui dans la majorité des cas. Notre calculateur cumule automatiquement l\'aide de l\'État, la prime énergie, le prêt sans intérêt, la TVA réduite et les aides locales.',
      },
      {
        question: 'Comment récupérez-vous mes données SIRET ?',
        answer:
          'Via le registre officiel des entreprises (données publiques). Hébergées en France, jamais transmises à des tiers commerciaux.',
      },
    ],
  },
  render: FaqSplit,
};
